import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import type { AutomationId } from "@repo/app-types/entities/automation";
import {
  buildAutomationReportPath,
  type AutomationArtifactSummary,
} from "@repo/app-types/entities/automationArtifact";
import type {
  StartAutomationPreviewRunToolInput,
  StartAutomationPreviewRunToolOutput,
} from "@repo/app-types/entities/automationConfigurationTools";
import {
  TERMINAL_AUTOMATION_RUN_STATUSES,
  type AutomationRunId,
  type AutomationRunStatus,
} from "@repo/app-types/entities/automationRun";
import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";
import { useCallback, useEffect, useState, type FC } from "react";
import { Link } from "react-router";
import { useAutomationArtifactsQuery } from "../../../api/entities/automationArtifact.js";
import { useAutomationRunByIdQuery } from "../../../api/entities/automationRun.js";
import { AutomationReportTile } from "../../automations/automationReportTile.js";
import {
  AutomationRunStreamProvider,
  useAutomationRunLifecycle,
} from "../../automations/automationRunStreamProvider.js";

/**
 * How often the run record is re-read while the preview is going. The run's
 * live stream carries each status change, so this is the fallback for a card
 * whose stream never connected — slow on purpose.
 */
const RUN_POLL_INTERVAL_MS = 15_000;

/**
 * The reports a finished preview saved, each a tile linking to its report page.
 * The chat carries no playbook in context, so the tiles link the playbook-free
 * report route, which resolves the report's automation and playbook itself.
 */
interface PreviewRunReportsProps {
  reports: readonly AutomationArtifactSummary[];
}

const PreviewRunReports: FC<PreviewRunReportsProps> = (props) => {
  const { reports } = props;
  return (
    <div className="flex flex-col gap-2">
      {reports.map((report) => (
        <Link
          key={report.id}
          to={buildAutomationReportPath(report.id)}
          className="block w-full max-w-md"
        >
          <AutomationReportTile title={report.title} isLinked />
        </Link>
      ))}
    </div>
  );
};

interface PreviewRunProgressProps {
  automationId: AutomationId;
  runId: AutomationRunId;
  /** The run record's status, until the live stream reports a newer one. */
  recordStatus: AutomationRunStatus;
  /** Called once the run is over, which stops the card's polling. */
  onRunFinished: () => void;
}

/**
 * The card's body, under the run's stream provider: a status line while the
 * preview is going, then the reports it saved.
 */
const PreviewRunProgress: FC<PreviewRunProgressProps> = (props) => {
  const { automationId, runId, recordStatus, onRunFinished } = props;
  const { status, isRunTerminal } = useAutomationRunLifecycle(recordStatus);

  useEffect(() => {
    if (isRunTerminal) {
      onRunFinished();
    }
  }, [isRunTerminal, onRunFinished]);

  // The run's reports exist only once it is over. A list fetched earlier in the
  // session predates this run, so refetch rather than read it from cache.
  const { data: artifacts, isError: hasArtifactsError } =
    useAutomationArtifactsQuery(isRunTerminal ? automationId : skipToken, {
      refetchOnMountOrArgChange: true,
    });

  if (!isRunTerminal) {
    return (
      <ChatToolBase status={{ type: "running" }}>
        {status === "pending" ? "Preview run queued" : "Running a preview"}
      </ChatToolBase>
    );
  }

  if (status !== "succeeded") {
    return (
      <ChatToolBase status={{ type: "incomplete" }}>
        {status === "canceled"
          ? "Preview run canceled"
          : "The preview run failed"}
      </ChatToolBase>
    );
  }

  if (hasArtifactsError) {
    return (
      <ChatToolBase status={{ type: "incomplete" }}>
        Error loading the preview&apos;s report
      </ChatToolBase>
    );
  }

  if (!artifacts) {
    return (
      <ChatToolBase status={{ type: "running" }}>
        Loading the preview&apos;s reports
      </ChatToolBase>
    );
  }

  // Empty reports are the run finding nothing worth reporting, and read as
  // that rather than as a report to open (the results list hides them too).
  const reports = artifacts.filter(
    (artifact) => artifact.automation_run_id === runId && !artifact.is_empty,
  );
  if (reports.length === 0) {
    return (
      <ChatToolBase status={{ type: "complete" }}>
        The preview found nothing to report
      </ChatToolBase>
    );
  }

  return <PreviewRunReports reports={reports} />;
};

/**
 * Follows the preview run the agent started, so the chat — not the agent —
 * waits for it: the run's own live stream drives the card while it is going,
 * and the run record is polled as a fallback until it is over.
 */
interface AutomationPreviewRunProps {
  automationId: AutomationId;
  runId: AutomationRunId;
}

const AutomationPreviewRun: FC<AutomationPreviewRunProps> = (props) => {
  const { automationId, runId } = props;
  const [isRunFinished, setIsRunFinished] = useState(false);
  const onRunFinished = useCallback(() => setIsRunFinished(true), []);
  const { data: run } = useAutomationRunByIdQuery(
    { automationId, automationRunId: runId },
    { pollingInterval: isRunFinished ? 0 : RUN_POLL_INTERVAL_MS },
  );

  if (!run) {
    return (
      <ChatToolBase status={{ type: "running" }}>
        Starting a preview run
      </ChatToolBase>
    );
  }

  return (
    // A run already terminal when the card mounts — a thread reopened after the
    // preview finished — has no live stream to join.
    <AutomationRunStreamProvider
      automationId={automationId}
      runId={runId}
      isEnabled={!TERMINAL_AUTOMATION_RUN_STATUSES.has(run.status)}
    >
      <PreviewRunProgress
        automationId={automationId}
        runId={runId}
        recordStatus={run.status}
        onRunFinished={onRunFinished}
      />
    </AutomationRunStreamProvider>
  );
};

/**
 * `start-automation-preview-run`: the run it started, tracked to its reports.
 * The tool itself only enqueues the run, so the card takes over from its
 * output — the agent neither waits for the preview nor reports what it found.
 */
export const StartAutomationPreviewRunTool: ToolComponent<
  StartAutomationPreviewRunToolInput,
  StartAutomationPreviewRunToolOutput | MastraErrorToolOutput
> = (props) => {
  const { input, output, status } = props;
  if (status.type === "running") {
    return <ChatToolBase status={status}>Starting a preview run</ChatToolBase>;
  }

  if (!output || checkIsMastraErrorToolOutput(output)) {
    return (
      <ChatToolBase status={{ type: "incomplete" }}>
        Couldn&apos;t start the preview run
      </ChatToolBase>
    );
  }

  return (
    <AutomationPreviewRun
      automationId={input.automation_id}
      runId={output.automation_run_id}
    />
  );
};
